import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import CodeBlock from "./CodeBlock";
import ZoomableImage from "./ImageLightbox";

// Source-line reference artifacts the LLM emits next to images / quotes,
// e.g. "[cimatron_crawl_v3_embed_images_20251007_212756.json:0-0]". Not a
// user-facing citation — strip from the rendered output.
const ARTIFACT_REF_RE = /\[[^\]\n]+?:\d+(?:-\d+)?\]/g;

// Preprocess markdown before handing to react-markdown:
//   1. Drop [file.ext:N-N] artifact refs (outside code spans).
//   2. Convert numeric [N] citation markers into a clickable <sup> pill.
function preprocessMarkdown(markdown) {
  if (!markdown) return "";
  const parts = markdown.split(/(```[\s\S]*?```|`[^`]*`)/g);
  return parts
    .map((part) => {
      if (!part) return part;
      if (part.startsWith("`")) return part;
      let out = part.replace(ARTIFACT_REF_RE, "");
      out = out.replace(
        /\[(\d+)\]/g,
        '<sup class="citation-ref" data-citation="$1">$1</sup>'
      );
      return out;
    })
    .join("");
}

const MarkdownMessage = ({ markdown, citations }) => {
  const handleCitationClick = (n) => {
    const idx = parseInt(n, 10) - 1;
    const c = citations && citations[idx];
    if (!c) return;
    const url =
      c.external_url && c.external_url.trim() !== "" ? c.external_url : c.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const processed = preprocessMarkdown(markdown || "");

  const components = {
    a: ({ node, children, href, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    ),
    img: ({ node, src, alt }) => <ZoomableImage src={src} alt={alt} />,
    pre: ({ node, children }) => {
      const codeChild = React.Children.toArray(children).find(
        (ch) => ch && ch.type === "code"
      );
      const className = codeChild?.props?.className || "";
      const lang = (className.match(/language-(\w+)/) || [])[1] || "text";
      return <CodeBlock language={lang}>{children}</CodeBlock>;
    },
    sup: ({ className, children, ...props }) => {
      if (className === "citation-ref") {
        const n = props["data-citation"];
        return (
          <sup
            className="citation-ref"
            onClick={(e) => {
              e.preventDefault();
              handleCitationClick(n);
            }}
            role="button"
            tabIndex={0}
          >
            {children}
          </sup>
        );
      }
      return (
        <sup className={className} {...props}>
          {children}
        </sup>
      );
    },
    table: ({ children }) => (
      <div className="md-table-wrap">
        <table>{children}</table>
      </div>
    ),
  };

  return (
    <div className="md-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeHighlight]}
        components={components}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
